import { useEffect, useMemo, useRef, useState } from "react";
import AmountSection from "./AmountSection";
import ManagePaymentMethodsModal from "./ManagePaymentMethodsModal";
import PaymentMethodSection from "./PaymentMethodSection";
import {
  OtherPaymentMethod,
  OtherPaymentMethods,
  PaymentProvider,
  SavedPaymentMethod,
} from "./types";
import AirwallexCardForm from "@/components/modal/AirwallexCardForm";
import {
  deletePaymentMethod,
  getWalletDetail,
  setDefaultPaymentMethod,
} from "@/api/payment";
import { useModalStore } from "@/hooks/useModalStore";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import { message } from "antd";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  amount: number | null;
  selectedPaymentMethod: SavedPaymentMethod | null;
  minimumAmount?: number;
  amountError?: string;
  paymentError?: string;
  onAmountChange: (amount: number | null) => void;
  onPaymentMethodChange: (method: SavedPaymentMethod | null) => void;
  otherPaymentMethods?: OtherPaymentMethods;
  onOtherPaymentMethodSelect: (
    method: OtherPaymentMethod,
    provider: PaymentProvider
  ) => Promise<void>;
}

const formatFeeLabel = (fee: any): string | undefined => {
  const feeRateBps = Number(fee?.feeRateBps);
  const fixedFeeCents = Number(fee?.fixedFeeCents);
  const hasFeeRate = Number.isFinite(feeRateBps);
  const hasFixedFee = Number.isFinite(fixedFeeCents);

  if (!hasFeeRate && !hasFixedFee) return undefined;

  const feeParts: string[] = [];

  if (hasFeeRate) {
    feeParts.push(`${Number((feeRateBps / 100).toFixed(2))}%`);
  }

  if (hasFixedFee) {
    feeParts.push(`$${(fixedFeeCents / 100).toFixed(2)}`);
  }

  return `${feeParts.join(" + ")} fee`;
};

const toSavedPaymentMethods = (
  source: any[] | undefined,
  provider: PaymentProvider
): SavedPaymentMethod[] => {
  const seenMethodIds = new Set<string>();

  return (source || []).reduce<SavedPaymentMethod[]>((methods, method, index) => {
    const card = method?.card || method;
    const expiryMonth = card?.exp_month || card?.expMonth || method?.expMonth;
    const expiryYear = card?.exp_year || card?.expYear || method?.expYear;
    const paymentMethodId =
      method?.id ||
      method?.paymentMethodId ||
      method?.stripePaymentMethodId ||
      method?.awxPaymentMethodId;
    const id =
      paymentMethodId ||
      `${provider}-${card?.last4 || method?.last4 || "card"}-${index}`;

    if (seenMethodIds.has(id)) return methods;

    seenMethodIds.add(id);
    methods.push({
      id,
      paymentMethodId,
      provider,
      brand: card?.brand || method?.cardBrand || method?.brand || "Card",
      last4: card?.last4 || method?.last4 || "----",
      expires:
        expiryMonth && expiryYear
          ? `${String(expiryMonth).padStart(2, "0")} / ${String(expiryYear).slice(-2)}`
          : undefined,
      feeLabel: formatFeeLabel(method?.fee),
      isDefault: method?.isDefault === true,
    });

    return methods;
  }, []);
};

export default function OnlinePayment(props: Props) {
  const {
    isOpen,
    amount,
    selectedPaymentMethod,
    minimumAmount,
    amountError,
    paymentError,
    onAmountChange,
    onPaymentMethodChange,
    otherPaymentMethods,
    onOtherPaymentMethodSelect,
  } = props;
  const { t } = useTranslation();
  const { walletDetail, setWalletDetail } = usePaymentStore();
  const { setShowModalPaymentBoundCard } = useModalStore();
  const [providerOverride, setProvider] = useState<PaymentProvider | null>(null);
  const [airwallexCardFormOpen, setAirwallexCardFormOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const amountSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) return;

    setProvider(null);
    setManageOpen(false);
    setAirwallexCardFormOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!amountError) return;

    const frame = window.requestAnimationFrame(() => {
      amountSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [amountError]);
  const stripeMethods = useMemo<SavedPaymentMethod[]>(
    () => toSavedPaymentMethods(walletDetail?.paymentMethods, "stripe"),
    [walletDetail?.paymentMethods]
  );

  const airwallexMethods = useMemo<SavedPaymentMethod[]>(() => {
    const sourceMethods = toSavedPaymentMethods(
      walletDetail?.awxPaymentMethods,
      "airwallex"
    );
    return sourceMethods;
  }, [walletDetail?.awxPaymentMethods]);

  const airwallexHasDefault = airwallexMethods.some(
    (method) => method.isDefault
  );
  const stripeHasDefault = stripeMethods.some((method) => method.isDefault);
  const provider = providerOverride ?? (
    !airwallexHasDefault && stripeHasDefault ? "stripe" : "airwallex"
  );

  const visibleMethods = useMemo(
    () => provider === "stripe"
      ? stripeMethods
      : airwallexMethods,
    [airwallexMethods, provider, stripeMethods]
  );

  const selectedId = selectedPaymentMethod?.id ?? null;

  const selectMethod = (id: string | null) => {
    const allMethods = [...airwallexMethods, ...stripeMethods];
    onPaymentMethodChange(allMethods.find((method) => method.id === id) ?? null);
  };

  // 打开弹窗、切换支付渠道或支付方式刷新后，优先选中当前渠道的默认卡。
  useEffect(() => {
    if (!isOpen) return;

    const selectedMethodIsVisible =
      selectedPaymentMethod?.provider === provider &&
      visibleMethods.some((method) => method.id === selectedId);

    if (!selectedMethodIsVisible) {
      onPaymentMethodChange(
        visibleMethods.find((method) => method.isDefault) ?? null
      );
    }
  }, [
    isOpen,
    onPaymentMethodChange,
    provider,
    selectedId,
    selectedPaymentMethod?.provider,
    visibleMethods,
  ]);

  const addMethod = () => {
    if (provider === "stripe") {
      setShowModalPaymentBoundCard(true);
      return;
    }

    setAirwallexCardFormOpen(true);
  };

  return (
    <div className="space-y-[18px] pb-6 md:space-y-7">
      <div ref={amountSectionRef} className="scroll-mt-6">
        <AmountSection
          amount={amount}
          onChange={onAmountChange}
          minimumAmount={minimumAmount}
          error={amountError}
        />
      </div>
      <PaymentMethodSection
        provider={provider}
        methods={visibleMethods}
        selectedId={selectedId}
        error={paymentError}
        onProviderChange={(next) => {
          const nextMethods =
            next === "stripe" ? stripeMethods : airwallexMethods;
          setProvider(next);
          onPaymentMethodChange(
            nextMethods.find((method) => method.isDefault) ?? null
          );
        }}
        onSelect={selectMethod}
        onManage={() => setManageOpen(true)}
        onAdd={addMethod}
        otherPaymentMethods={
          otherPaymentMethods?.[provider === "airwallex" ? "awx" : "stripe"]
        }
        onOtherPaymentMethodSelect={(method) =>
          onOtherPaymentMethodSelect(method, provider)
        }
      />
      <ManagePaymentMethodsModal
        open={manageOpen}
        methods={visibleMethods}
        onClose={() => setManageOpen(false)}
        onAdd={() => {
          setManageOpen(false);
          addMethod();
        }}
        onRemove={async (id, methodProvider) => {
          if (!methodProvider) return;
          await deletePaymentMethod(
            id,
            methodProvider === "airwallex" ? "awx" : "stripe"
          );
          try {
            const updatedWalletDetail = await getWalletDetail({
              forceRefresh: true,
            });
            setWalletDetail(updatedWalletDetail);
            message.success(
              t("components.chargeModal.managePaymentMethods.removeSuccess", {
                defaultValue: "Payment method removed.",
              })
            );
          } catch {
            message.warning(
              t(
                "components.chargeModal.managePaymentMethods.removeSuccessRefreshFailed",
                {
                  defaultValue:
                    "Payment method removed. Refresh the page to view the latest list.",
                }
              )
            );
          }
          if (selectedId === id) selectMethod(null);
        }}
        onSetDefault={async (id, methodProvider) => {
          if (!methodProvider) return;
          await setDefaultPaymentMethod(
            id,
            methodProvider === "airwallex" ? "awx" : "stripe"
          );
          try {
            const updatedWalletDetail = await getWalletDetail({
              forceRefresh: true,
            });
            setWalletDetail(updatedWalletDetail);
            message.success(
              t("components.chargeModal.managePaymentMethods.setDefaultSuccess", {
                defaultValue: "Default payment method updated.",
              })
            );
            selectMethod(id);
          } catch {
            message.warning(
              t(
                "components.chargeModal.managePaymentMethods.setDefaultSuccessRefreshFailed",
                {
                  defaultValue:
                    "Default payment method updated. Refresh the page to view the latest list.",
                }
              )
            );
          }
        }}
      />
      <AirwallexCardForm
        visible={airwallexCardFormOpen}
        onClose={() => setAirwallexCardFormOpen(false)}
      />
    </div>
  );
}
